import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Table } from '@/components/ui/Table';
import { NewBacktestDialog } from '@/components/backtests/NewBacktestDialog';
import {
  formatDateTime,
  formatSigned,
  getBacktestStatusTone,
  getNetPnl,
  getReturnPercent,
  isCertifiedRun,
  isActiveRun,
} from '@/components/backtests/backtestPresentation';
import { useBacktests, useDatasets, useStrategies } from '@/api/queries';
import type { BacktestRunDto } from '@/api/types';
import styles from './BacktestsPage.module.css';
import { CREATE_SOURCE_PARAMS, routes, withoutParams } from '@/lib/routePaths';

type StatusFilter = 'All' | 'Queued' | 'Running' | 'Succeeded' | 'Failed';

export default function BacktestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: backtests, isLoading, error } = useBacktests({
    refetchInterval: (query) => query.state.data?.some(isActiveRun) ? 2_000 : false,
  });
  const { data: datasets } = useDatasets();
  const { data: strategies } = useStrategies();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('All');

  const datasetNames = useMemo(
    () => new Map((datasets ?? []).map((dataset) => [
      dataset.id,
      `${dataset.displayName ?? dataset.id} (${dataset.instrument ?? 'unknown'})`,
    ])),
    [datasets],
  );
  const filteredRuns = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (backtests ?? []).filter((run) => {
      if (status !== 'All' && run.status !== status) return false;
      if (!query) return true;
      const datasetText = runDatasetIds(run).map((id) => datasetNames.get(id) ?? id).join(' ');
      return `${run.strategyName ?? ''} ${datasetText} ${run.id ?? ''}`.toLowerCase().includes(query);
    });
  }, [backtests, datasetNames, search, status]);

  return (
    <div className={styles.page}>
      <div className={styles.headingRow}>
        <div>
          <h1>Backtests</h1>
          <p>Queue strategy runs, monitor progress, and inspect results from one workspace.</p>
        </div>
        <Button type="button" variant="primary" onClick={() => setSearchParams((current) => {
          const next = new URLSearchParams(current);
          next.set('create', '1');
          return next;
        })}>
          <Plus size={16} /> New backtest
        </Button>
      </div>

      <Card>
        <div className={styles.filters}>
          <Input
            label="Search runs"
            placeholder="Strategy, dataset, or run ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value as StatusFilter)}
            options={['All', 'Queued', 'Running', 'Succeeded', 'Failed'].map((value) => ({ value, label: value }))}
          />
          <span className={styles.resultCount}>{filteredRuns.length.toLocaleString()} runs</span>
        </div>

        {isLoading && <p className="statusText statusTextMuted">Loading backtests…</p>}
        {error && <p className="statusText statusTextDanger">{error.message}</p>}
        {!isLoading && !error && filteredRuns.length === 0 && (
          <div className={styles.emptyState}>
            <strong>{backtests?.length ? 'No runs match these filters.' : 'No backtests yet.'}</strong>
            <span>{backtests?.length ? 'Try a different search or status.' : 'Create a run to start evaluating a strategy.'}</span>
          </div>
        )}
        {filteredRuns.length > 0 && (
          <Table headers={['Strategy / Dataset', 'Status', 'Net P&L / Return', 'Completed trades', 'Max drawdown', 'Created', '']}>
            {filteredRuns.map((run) => (
              <RunRow
                key={run.id ?? `${run.datasetId}-${run.createdUtc}`}
                run={run}
                datasetName={runDatasetSummary(run, datasetNames)}
              />
            ))}
          </Table>
        )}
      </Card>

      {searchParams.get('create') === '1' && (
        <NewBacktestDialog
          datasets={datasets ?? []}
          strategies={strategies ?? []}
          initialDatasetIds={searchParams.get('datasetId') ? [searchParams.get('datasetId')!] : undefined}
          initialStrategyId={searchParams.get('strategyId') ?? undefined}
          onClose={() => setSearchParams((current) => withoutParams(current, CREATE_SOURCE_PARAMS), { replace: true })}
        />
      )}
    </div>
  );
}

function runDatasetIds(run: BacktestRunDto) {
  return run.datasetIds?.length ? [...run.datasetIds] : run.datasetId ? [run.datasetId] : [];
}

function runDatasetSummary(run: BacktestRunDto, names: ReadonlyMap<string | null | undefined, string | null | undefined>) {
  const ids = runDatasetIds(run);
  if (ids.length === 1) return names.get(ids[0]) ?? ids[0];
  return ids.length > 1 ? `${ids.length} datasets` : 'Unknown dataset';
}

function RunRow({ run, datasetName }: { run: BacktestRunDto; datasetName?: string | null }) {
  const net = getNetPnl(run);
  const returnPercent = getReturnPercent(run);
  return (
    <tr>
      <td>
        <div className={styles.primaryCell}>
          <strong>{run.strategyName ?? 'Unnamed strategy'}</strong>
          <span>{datasetName ?? run.datasetId ?? 'Unknown dataset'}</span>
          {run.sourceRunId && run.id &&
            <small><Link to={routes.backtest(run.sourceRunId)}>Live replay of {run.sourceRunId.slice(0, 8)}</Link></small>}
          {run.status === 'Succeeded' && (run.executionErrorCount ?? 0) > 0 &&
            <small>{run.executionErrorCount} execution error(s)—results are not certified</small>}
          {run.status === 'Succeeded' && !isCertifiedRun(run) && run.executionErrorCount == null &&
            <small>Legacy execution model—rerun to certify</small>}
        </div>
      </td>
      <td>
        <Badge variant={(run.executionErrorCount ?? 0) > 0 ? 'warning' : getBacktestStatusTone(run.status)}>
          {(run.executionErrorCount ?? 0) > 0 ? 'Completed with execution errors' : run.status ?? 'Unknown'}
        </Badge>
        {isActiveRun(run) && <div className={styles.progressText}>{Math.round((run.progress ?? 0) * 100)}%</div>}
      </td>
      <td className={styles.mono}>
        {net == null ? '—' : <span className={net >= 0 ? styles.positive : styles.negative}>{formatSigned(net)}</span>}
        <small>{returnPercent == null ? '' : `${formatSigned(returnPercent)}%`}</small>
      </td>
      <td className={styles.mono}>{run.totalTrades ?? 0}</td>
      <td className={styles.mono}>
        {run.maxDrawdown == null ? '—' : run.maxDrawdown.toFixed(2)}
        <small>{run.maxDrawdownPercent == null ? '' : `${run.maxDrawdownPercent.toFixed(2)}%`}</small>
      </td>
      <td className={styles.mono}>{formatDateTime(run.createdUtc)}</td>
      <td>
        {run.id && (
          <Link to={routes.backtest(run.id)}>
            <Button type="button" variant="outline-info"><Eye size={14} /> View</Button>
          </Link>
        )}
      </td>
    </tr>
  );
}
